<template>
	<details v-for="(item, index) in props.items" :key="index" class="c_details" @toggle="onClickToggle">
		<summary class="listItem_summary">
			<div class="listItem">
				<div class="bookmark">
					<IconThinStar
						width="24"
						height="24"
						:icon-color="!item.bookmark ? '#D9D9D9' : '#FFB800'"
						@click.prevent
						@click.stop="onClickBookmark(item.event_pk, item.bookmark)"
					/>
				</div>
				<div class="c_inner">
					<div class="item_pick">
						<img :src="item.file_path" alt="기업 로고" loading="lazy" />
						<div class="item_title">
							<span>{{ item.event_name }}</span
							><span>{{ item.event_ticker }}</span>
						</div>
					</div>
					<div class="item_total">{{ item.event_score }}점</div>
				</div>
			</div>
		</summary>
		<ul class="listItem_bottom">
			<li v-for="(m, masterIndex) in item.master" :key="masterIndex" class="listItem_bottom_content">
				<div class="content_master">
					<img :src="m.file_path" alt="대가이미지" loading="lazy" />
					<p class="master_name">{{ m.master_name }}</p>
				</div>
			</li>
			<div class="btn_box">
				<button
					class="c_button primary-fill radius-full height-small btn-research"
					@click="onClickResearch(item.event_pk)"
				>
					분석
				</button>
			</div>
		</ul>
	</details>
</template>

<script setup lang="ts">
// core
import { useRouter } from 'vue-router';

import { GetHotrankingValue } from '@/api/hotRanking';
// assets
import { useAdjustBookmarksApi } from '@/api/Interest';
import { COMPANY_TITLE, MESSAGE_CANCEL_COMPLETED, UNFAVORITE_PROMPT } from '@/constants/title';
import { useHeaderStore } from '@/stores/header';
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';

import IconThinStar from '../atom/icon/IconThinStar.vue';

// -----variable-----
const props = defineProps<{
	items?: GetHotrankingValue[];
}>();
const emit = defineEmits(['update:bookMarkRefetch']);

const popupStore = usePopupStore();
const toastStore = useToastStore();
const router = useRouter();
const headerStore = useHeaderStore();

// -----api-----
const { mutateAsync: adjustBookmarks } = useAdjustBookmarksApi();

// -----function-----
// 다중 열림 방지 기능
const onClickToggle = (e: Event) => {
	const toggled = e.target as HTMLDetailsElement;
	if (toggled && toggled.hasAttribute('open')) {
		toggled.classList.add('open');
		document.querySelectorAll('details[open]').forEach(function (opened) {
			if (toggled !== opened) {
				(opened as HTMLDetailsElement).removeAttribute('open');
				(opened as HTMLDetailsElement).classList.remove('open');
			}
		});
	} else {
		toggled.classList.remove('open');
	}
};
// 개별 북마크 클릭 시 실행 함수
const onClickBookmark = async (eventPk: number, bookmark: number) => {
	const params = [
		{
			event_pk: eventPk,
			master_pk: null,
		},
	];
	if (bookmark) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: UNFAVORITE_PROMPT,
			confirm: async () => {
				const res = await adjustBookmarks({
					bookmarks: params,
				});
				if (res.data.success) {
					toastStore.openToast({
						subContent: COMPANY_TITLE,
						content: MESSAGE_CANCEL_COMPLETED,
					});
					emit('update:bookMarkRefetch');
				}
			},
		});
	} else {
		const res = await adjustBookmarks({
			bookmarks: params,
		});
		if (res.data.success) {
			emit('update:bookMarkRefetch');
		}
	}
};
const onClickResearch = (eventPk: number) => {
	if (headerStore.getCountry === 'USA') {
		router.push({
			path: `/report/usa/${eventPk}`,
			query: { tab: '개요' },
		});
	} else if (headerStore.getCountry === 'JPN') {
		router.push({
			path: `/report/jpn/${eventPk}`,
			query: { tab: '개요' },
		});
	}
};
</script>

<style lang="scss" scoped>
.listItem_summary {
	padding-right: var(--space-mid);
	background-color: var(--color-bg-mid);
	border-radius: var(--space-x-small);
	gap: 0.8rem;
	.listItem {
		display: flex;
		width: 100%;
		color: var(--color-base-white);
		white-space: nowrap;
		min-width: 0;
		.bookmark {
			border-radius: var(--space-x-small) 0 0 var(--space-x-small);
			display: flex;
			align-items: center;
			justify-content: center;
			background-color: var(--color-bg-light);
			font-weight: var(--font-w-semi-bold);
			font-size: var(--space-large);
			line-height: 2.387rem;
			padding: 0.8rem;
		}
		.c_inner {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: var(--space-mid) 0rem var(--space-mid) 1rem;
			max-width: none;
			min-width: 0;
			.item_pick {
				display: flex;
				align-items: center;
				gap: 0.6rem;
				min-width: 0;
				img {
					width: 3.2rem;
					height: 3.2rem;
					border-radius: 50%;
				}
				.item_title {
					display: flex;
					flex-direction: column;
					min-width: 0;
					:nth-child(1) {
						display: block;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						font-size: var(--font-s-mid);
						color: var(--color-base-white);
						font-weight: var(--font-w-semi-bold);
						line-height: normal;
					}
					:nth-child(2) {
						display: block;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						font-size: var(--font-s-small);
						color: var(--color-base-light);
						font-weight: var(--font-w-light);
						line-height: normal;
					}
				}
			}
			.item_total {
				color: var(--color-danger);
				font-weight: var(--font-w-semi-bold);
				font-size: var(--font-s-regular);
				line-height: 2.1rem;
			}
		}
	}
}
.listItem_bottom {
	display: flex;
	flex-direction: column;
	align-items: center;
	background-color: var(--color-bg-mid);
	border-radius: 0rem 0rem var(--space-x-small) var(--space-x-small);
	padding: var(--space-mid);
	gap: 0.8rem;
	&_content {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: flex-start;
		width: 100%;
		.content_master {
			display: flex;
			align-items: center;
			gap: 1rem;
			img {
				width: 5.8rem;
				height: 5.8rem;
			}
			.master_name {
				font-size: var(--font-s-mid);
				color: var(--color-base-white);
				font-weight: var(--font-w-semi-bold);
				line-height: normal;
			}
		}
	}
	.btn_box {
		display: flex;
		height: 3.2rem;
		.btn-research {
			display: flex;
			padding: 6px 16px;
			justify-content: center;
			align-items: center;
			width: 8rem;
			gap: 10px;
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			line-height: normal;
		}
	}
}
.c_details[open] .listItem_summary .bookmark {
	border-radius: 0.8rem 0 0 0;
}
.c_details[open] .listItem_summary {
	border-radius: var(--space-x-small) var(--space-x-small) 0rem;
}
</style>
